import { Editor, Transforms, Element as SlateElement, } from 'slate'

const LIST_TYPES = ['numbered-list', 'bulleted-list']
export const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify']

export const parseDamianHTMLToSlateJSON = (input) => {
  const div = document.createElement('div');
  div.innerHTML = input;

  const result = [];

  function parseNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      result.push({ text: node.textContent });
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const styleObj = { text: node.textContent };
      if (node.tagName.toLowerCase() === 'b') {
        styleObj.bold = true;
      }
      if (node.tagName.toLowerCase() === 'i') {
        styleObj.italic = true;
      }
      result.push(styleObj);
    }
  }

  div.childNodes.forEach(parseNode);
  return [{
    type: 'paragraph',
    children: result
  }]
}


export const parseHTMLToSlateJSON = (html) => {
  if (!html) {
    return [{
      type: 'paragraph',
      children: [{ text: '' }]
    }]
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  function parseElement(node) {
    let element = { type: '', children: [] };
    if (node.nodeName === 'P') {
      element.type = 'paragraph';
    } else if (node.nodeName === 'H1') {
      element.type = 'heading-one';
    } else if (node.nodeName === 'H2') {
      element.type = 'heading-two';
    } else if (node.nodeName === 'BLOCKQUOTE') {
      element.type = 'block-quote';
    } else if (node.nodeName === 'OL') {
      element.type = 'numbered-list';
    } else if (node.nodeName === 'UL') {
      element.type = 'bulleted-list';
    } else if (node.nodeName === 'LI') {
      element.type = 'list-item';
    } else if (node.nodeName === '#text') {
      return {
        type: 'paragraph',
        children: [{ text: node.textContent }]
      };
    } else {
      return null;
    }

    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE || child.nodeName === 'SPAN' || child.nodeName === 'B' || child.nodeName === 'EM' || child.nodeName === 'U') {
        element.children.push(parseTextNode(child));
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        let parsedChild = parseElement(child);
        if (parsedChild) {
          element.children.push(parsedChild);
        }
      }
    });

    if (!node.firstChild) {
      element.children.push({ text: '' })
    }

    if (node.style.textAlign) {
      element.align = node.style.textAlign;
    }

    return element;
  }

  function parseTextNode(node) {
    let text = { text: node.textContent };
    let child = node;

    while (child) {
      if (child.tagName === 'B') text.bold = true;
      if (child.tagName === 'EM') text.italic = true;
      if (child.tagName === 'U') text.underline = true;
      child = child.firstChild;
    }

    return text;
  }

  let jsonResult = [];
  body.childNodes.forEach(node => {
    let parsedElement = parseElement(node);
    if (parsedElement) {
      jsonResult.push(parsedElement);
    }
  });

  return jsonResult;
}

export const parseSlateJSONToHTML = (json, returnEmptyStringIfContentIsEmpty = true) => {
  function createTextNode(textObj) {
    let textContent = textObj.text;
    if (textObj.bold) {
      textContent = `<b>${textContent}</b>`;
    }
    if (textObj.italic) {
      textContent = `<em>${textContent}</em>`;
    }
    if (textObj.underline) {
      textContent = `<u>${textContent}</u>`;
    }
    return textContent
  }

  function createElement(node) {
    let tag = '';
    switch (node.type) {
      case 'paragraph':
        tag = 'p';
        break;
      case 'heading-one':
        tag = 'h1';
        break;
      case 'heading-two':
        tag = 'h2';
        break;
      case 'block-quote':
        tag = 'blockquote';
        break;
      case 'numbered-list':
        tag = 'ol';
        break;
      case 'bulleted-list':
        tag = 'ul';
        break;
      case 'list-item':
        tag = 'li';
        break;
      default:
        return '';
    }

    let content = '';
    node.children.forEach(child => {
      if (child.type) {
        content += createElement(child); // Rekursywnie obsługuj dzieci
      } else {
        content += createTextNode(child); // Obsługa węzłów tekstowych
      }
    });

    let style = '';
    if (node.align) {
      style = ` style="text-align: ${node.align};"`;
    }

    return `<${tag}${style}>${content}</${tag}>`;
  }

  if (returnEmptyStringIfContentIsEmpty && json.length === 1 && json[0].children.length === 1 && !json[0].children[0].text) {
    return ''
  }

  let htmlResult = '';
  json.forEach(node => {
    htmlResult += createElement(node);
  });

  return htmlResult
}

export const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  )
  const isList = LIST_TYPES.includes(format)

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  })
  let newProperties
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    }
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    }
  }
  Transforms.setNodes(editor, newProperties)

  if (!isActive && isList) {
    const block = { type: format, children: [] }
    Transforms.wrapNodes(editor, block)
  }
}

export const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format)

  if (isActive) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}

export const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor
  if (!selection) return false

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  )

  return !!match
}

export const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor)
  return marks ? marks[format] === true : false
}